/*
 * The shell both date pickers share: a field-look trigger, and the surface it
 * opens — an anchored popover on a desktop, a bottom sheet on a phone. Base UI
 * (the headless library both apps already run) traps focus inside, closes on
 * Escape and hands focus back to the trigger.
 */
import { Dialog } from "@base-ui/react/dialog";
import { Popover } from "@base-ui/react/popover";
import * as React from "react";

import { cn } from "./cn";
import { buttonRecipe, focusRing, popoverRecipe, sheetRecipe } from "./recipes";

const PHONE = "(max-width: 639px)";
const WIDE = "(min-width: 768px)";

function useMedia(query: string): boolean {
  return React.useSyncExternalStore(
    (notify) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const list = window.matchMedia(query);
      list.addEventListener?.("change", notify);
      return () => list.removeEventListener?.("change", notify);
    },
    () =>
      typeof window !== "undefined" && !!window.matchMedia
        ? window.matchMedia(query).matches
        : false,
    () => false,
  );
}

/**
 * Which trigger edge the popover lines up with: its start edge when the
 * trigger sits in the left half of the viewport, its end edge in the right
 * half, so a wide panel opens back over the content instead of off the
 * screen or over the sidebar.
 */
export function popoverAlign(
  trigger: { left: number; right: number },
  viewportWidth: number,
): "start" | "end" {
  if (viewportWidth <= 0) return "start";
  return (trigger.left + trigger.right) / 2 > viewportWidth / 2
    ? "end"
    : "start";
}

/** A phone gets the sheet; anything wider than a tablet gets two months. */
export function usePickerLayout() {
  return { phone: useMedia(PHONE), wide: useMedia(WIDE) };
}

const calendarIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 16 16"
    className="size-4 shrink-0 text-muted-foreground"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="12" height="11" rx="2" />
    <path d="M2 6.5h12M5.5 1.75v2.5M10.5 1.75v2.5" />
  </svg>
);

const chevronDown = (
  <svg
    aria-hidden="true"
    viewBox="0 0 16 16"
    className="size-4 text-muted-foreground"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m4 6 4 4 4-4" />
  </svg>
);

type PickerSize = "default" | "sm";

type PickerTriggerProps = Omit<
  React.ComponentPropsWithoutRef<"button">,
  "children"
> & {
  text: string;
  /** Muted when nothing is chosen yet. */
  placeholder?: boolean;
  size?: PickerSize;
  /** Reserve the right slot for the clear button instead of the chevron. */
  clearable?: boolean;
};

/** A button that reads as a field: calendar icon, the value, a chevron. */
export const PickerTrigger = React.forwardRef<
  HTMLButtonElement,
  PickerTriggerProps
>(function PickerTrigger(
  { text, placeholder, size = "default", clearable, className, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      data-slot="date-picker-trigger"
      className={cn(
        buttonRecipe.base,
        buttonRecipe.look.field,
        size === "sm"
          ? "h-8 gap-2 pr-8 pl-2.5 text-sm"
          : "h-control gap-2 pr-9 pl-control-x",
        "w-full max-w-full justify-start active:scale-100",
        placeholder && "text-muted-foreground",
        className,
      )}
      {...props}
    >
      {calendarIcon}
      <span className="min-w-0 truncate">{text}</span>
      {clearable ? null : (
        <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2">
          {chevronDown}
        </span>
      )}
    </button>
  );
});

/** The × that sits in the trigger's right slot; a sibling, never nested. */
export function ClearButton({
  label,
  onClear,
}: {
  label: string;
  onClear: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClear}
      className={cn(
        "absolute top-1/2 right-1.5 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-control text-muted-foreground hover:bg-muted hover:text-foreground",
        focusRing,
      )}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 16 16"
        className="size-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      >
        <path d="m4.5 4.5 7 7m0-7-7 7" />
      </svg>
    </button>
  );
}

type PickerSurfaceProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phone: boolean;
  /** The trigger, rendered by the surface's own Trigger part. */
  trigger: React.ReactElement<Record<string, unknown>>;
  /** Names the dialog for a screen reader ("Choose a date range"). */
  title: string;
  /** CSS selector, inside the surface, for what takes focus on open. */
  focusOnOpen: string;
  children: React.ReactNode;
};

export function PickerSurface({
  open,
  onOpenChange,
  phone,
  trigger,
  title,
  focusOnOpen,
  children,
}: PickerSurfaceProps) {
  const popupRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [align, setAlign] = React.useState<"start" | "end">("start");
  const initialFocus = () =>
    popupRef.current?.querySelector<HTMLElement>(focusOnOpen) ?? true;
  if (phone) {
    return (
      <Dialog.Root open={open} onOpenChange={(next) => onOpenChange(next)}>
        <Dialog.Trigger render={trigger} />
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-50 bg-scrim transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0" />
          <Dialog.Popup
            data-side="bottom"
            ref={popupRef}
            data-slot="date-picker-sheet"
            initialFocus={initialFocus}
            className={cn(
              sheetRecipe.content,
              "max-h-[92dvh] gap-0 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))] data-ending-style:translate-y-10 data-starting-style:translate-y-10",
            )}
          >
            <Dialog.Title className="sr-only">{title}</Dialog.Title>
            <div
              aria-hidden="true"
              className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-pill bg-border"
            />
            {children}
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (next && rect) setAlign(popoverAlign(rect, window.innerWidth));
        onOpenChange(next);
      }}
      modal="trap-focus"
    >
      <Popover.Trigger ref={triggerRef} render={trigger} />
      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          align={align}
          sideOffset={6}
          collisionPadding={16}
          // Stay under (or, without room, over) the trigger: never beside it,
          // where a wide panel lands on the sidebar.
          collisionAvoidance={{
            side: "flip",
            align: "flip",
            fallbackAxisSide: "none",
          }}
          className="isolate z-50"
        >
          <Popover.Popup
            ref={popupRef}
            data-slot="date-picker-popover"
            aria-label={title}
            initialFocus={initialFocus}
            className={cn(
              popoverRecipe.content,
              "max-h-(--available-height) w-auto max-w-[calc(100vw-2rem)] gap-0 overflow-y-auto p-0",
            )}
          >
            {children}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** The Cancel button: the surface's own Close part, so touch can always leave. */
export function PickerClose({
  phone,
  children,
  className,
}: {
  phone: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const Close = phone ? Dialog.Close : Popover.Close;
  return (
    <Close
      className={cn(
        buttonRecipe.base,
        buttonRecipe.look.ghost,
        buttonRecipe.size.sm,
        className,
      )}
    >
      {children}
    </Close>
  );
}

export type { PickerSize };
